import { styled as muiStyled } from '@mui/material/styles'
import Layout from '@/layouts/index'
import Page from '@/components/Page'
import { useRouter } from 'next/router'
import pageService, { GlobalSetting, SeoMetadata, settingService } from '@/services/page'
import pathService from '@/services/path'
import { shinyCssWithoutHoverEffect } from '@/theme/palette'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { APPS } from '@/appConfig'
import type { ParsedUrlQuery } from 'querystring'
import {
  useTheme,
  Autocomplete,
  TextField,
  Typography,
  Container,
  Card,
  Grid,
  Divider,
  Button,
  Link,
  Box,
  Stack,
} from '@mui/material'
import { PromptSupportingLanguageMap } from '@/generated/graphql.prompts.node'
import { LoadingButton } from '@mui/lab'
import { useState, useMemo } from 'react'
import { $enum } from 'ts-enum-util'
import {
  useVisitorGeneratePromptLazyQuery,
  useVisitorFetchPromptUsagesQuery,
} from '@/generated/graphql'
import { RootStyle } from '@/sections/landing/Shares'
import Typewriter from '@/components/Typewriter'
import { PATH_APP_SERVICES } from '@/routes/paths'
import CopyClipboard from '@/components/CopyClipboard'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { DISCORD } from '@/config'

// TODO get prompt id from constant propmt
const LINE_STICKER_PROMPT_ID = 'line_sticker'

interface PathProps extends ParsedUrlQuery {
  appId: string
}

interface IndexProps {
  title?: string
  globalSetting?: GlobalSetting
  seoMetadataItemsCollection?: SeoMetadata[]
}

LineSticker.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

enum Character {
  Cat = 'Cat',
  Bear = 'Bear',
  Rabbit = 'Rabbit',
  Fox = 'Fox',
  Girl = 'Girl',
  Boy = 'Boy',
  SpongeBob = 'SpongeBob',
  MickeyMouse = 'Mickey Mouse',
}

const StyledCard = muiStyled(Card)`
  ${shinyCssWithoutHoverEffect}
  min-height: 100vh;
`

export default function LineSticker({ title, seoMetadataItemsCollection }: IndexProps) {
  const router = useRouter()
  const [
    visitorGeneratePromptLazyQuery,
    { loading: visitorGeneratePromptLazyQueryLoading, data: visitorGeneratePromptLazyQueryData },
  ] = useVisitorGeneratePromptLazyQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  })

  const { data: visitorFetchPromptUsagesQuery } = useVisitorFetchPromptUsagesQuery({
    variables: {
      where: {
        promptId: {
          _eq: LINE_STICKER_PROMPT_ID,
        },
      },
      limit: 30,
    },
  })
  const theme = useTheme()
  const [character, setCharacter] = useState<Character | string>('')
  const [originTextInputs, setOriginTextInputs] = useState<string[] | null>(null)
  const [textInputs, setTextInputs] = useState<string[] | null>(null)

  const renderText =
    visitorGeneratePromptLazyQueryData?.generatePrompt.createResponse?.choices[0]?.text

  const mergedTextInputs = useMemo(
    () => textInputs || renderText?.split(/\r?\n|\r|\n/g),
    [textInputs, renderText]
  )
  const handleGeneratePrompt = () =>
    visitorGeneratePromptLazyQuery({
      variables: {
        promptId: LINE_STICKER_PROMPT_ID,
        language: PromptSupportingLanguageMap.English,
        prompt: character,
      },
      fetchPolicy: 'no-cache',
    }).then(() => {
      setTextInputs(null)
      setOriginTextInputs(null)
    })

  const promptUsages = visitorFetchPromptUsagesQuery?.promptUsages ?? []

  const handleOpenMidjourneyBotChannel = () => {
    router.replace(
      DISCORD.linkToApp(
        DISCORD.server.mataverseDigital.id,
        DISCORD.server.mataverseDigital.channelId.midjourneyBot
      )
    )
  }

  return (
    <Page title={title ?? ''} metadataItems={seoMetadataItemsCollection}>
      <RootStyle theme={theme} backgroundColor="">
        <Container>
          <StyledCard sx={{ p: 5 }}>
            <Typography sx={{ mb: 3 }} color="primary" variant="h3">
              Line Sticker Generator
            </Typography>
            <Typewriter
              hideCursor
              styleOverPropsForText={{
                sx: {
                  color: 'common.white',
                },
                variant: 'body1',
              }}
              input="Make money confidently using the Line Sticker Generator. With a user-friendly interface design, you can design unique, fun stickers quickly and generate steady revenue from your creative work! Try it now to make money and have fun with your designs."
            />
            <Grid container spacing={4} sx={{ pt: 4, pb: 5 }}>
              <Grid item xs={12}>
                <Autocomplete
                  disablePortal
                  freeSolo
                  id="character"
                  inputValue={character}
                  options={$enum(Character).getValues()}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Character to generate"
                      helperText="Please choose one or typing your own"
                    />
                  )}
                  onInputChange={(_event, values) => setCharacter(values)}
                />
              </Grid>
              <Grid item xs={12}>
                <LoadingButton
                  variant="outlined"
                  fullWidth
                  size="large"
                  loading={visitorGeneratePromptLazyQueryLoading}
                  disabled={!character}
                  onClick={handleGeneratePrompt}
                >
                  Generate
                </LoadingButton>
              </Grid>
              <Grid item xs={12}>
                {visitorGeneratePromptLazyQueryData && renderText && (
                  <>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={15}
                      value={(originTextInputs ?? mergedTextInputs)?.join('\r\n')}
                      onChange={(event) => {
                        setOriginTextInputs(event.target.value.split(/\r?\n|\r|\n/g))
                        setTextInputs(event.target.value.split(/\r?\n|\r|\n/g))
                      }}
                    />
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 3 }}
                      size="large"
                      onClick={() => handleOpenMidjourneyBotChannel()}
                    >
                      Midjourney Channel
                    </Button>
                    <Divider sx={{ mt: 3 }}>
                      <Typography variant="h6">Copy and paste to Midjourney AI</Typography>
                    </Divider>
                    {mergedTextInputs &&
                      mergedTextInputs?.map((textInput, index) => {
                        const generateImageCommand = '/imagine prompt:'
                        let imagesPageSearch = textInput.split(generateImageCommand)[1]?.trim()
                        if (imagesPageSearch?.slice(-1) === '.') {
                          imagesPageSearch = imagesPageSearch.slice(0, -1)
                        }
                        return (
                          <>
                            {textInput.trim() && (
                              <>
                                <CopyClipboard
                                  key={textInput}
                                  shareInputValue={textInput}
                                  inputProps={{ sx: { mt: 3 } }}
                                  onShareInputValueChange={(val) => {
                                    setOriginTextInputs((prev) => {
                                      if (!prev) {
                                        prev = mergedTextInputs
                                      }
                                      const shallowCloneTextInputs = [...prev]
                                      shallowCloneTextInputs[index] = val
                                      return shallowCloneTextInputs
                                    })
                                  }}
                                />
                                {imagesPageSearch && (
                                  <Stack
                                    direction="row"
                                    spacing={3}
                                    sx={{ mt: 1 }}
                                    justifyContent="space-between"
                                  >
                                    <Link
                                      href={`${PATH_APP_SERVICES.midjourneyImageCollection}?search=${imagesPageSearch}`}
                                      target="_blank"
                                      rel="noopener"
                                    >
                                      <Button variant="outlined" fullWidth size="large">
                                        <Typography variant="subtitle1">
                                          Search for images related to this prompt
                                        </Typography>
                                      </Button>
                                    </Link>
                                    <CopyToClipboard
                                      text={`${generateImageCommand} ${imagesPageSearch}`}
                                      onCopy={() => {
                                        handleOpenMidjourneyBotChannel()
                                      }}
                                    >
                                      <Button variant="outlined" size="large">
                                        Copy And Go
                                      </Button>
                                    </CopyToClipboard>
                                  </Stack>
                                )}
                              </>
                            )}
                          </>
                        )
                      })}
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      sx={{ mt: 5 }}
                      onClick={() => router.push(PATH_APP_SERVICES.midjourneyImageCollection)}
                    >
                      Find your AI masterpieces after generation
                    </Button>
                  </>
                )}
              </Grid>
              {promptUsages.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 3 }} />
                  <Typography sx={{ mb: 3 }} color="primary" variant="h4">
                    Latest Generated Prompts
                  </Typography>
                  {promptUsages.map((promptUsage) =>
                    promptUsage.response.choices[0]?.text ? (
                      <Box key={promptUsage.id} mb={4}>
                        <Typography variant="body2" mb={1}>
                          Character: {promptUsage.userPrompt}
                        </Typography>
                        <CopyClipboard
                          key={promptUsage.response.choices[0]?.text}
                          shareInputValue={promptUsage.response.choices[0]?.text}
                        />
                      </Box>
                    ) : (
                      <></>
                    )
                  )}
                </Grid>
              )}
            </Grid>
          </StyledCard>
        </Container>
      </RootStyle>
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getDefaultPaths } = pathService()
  const paths = APPS.flatMap((app) => getDefaultPaths(app.id))

  return {
    paths,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<IndexProps, PathProps> = async ({ params, locale }) => {
  if (!params) throw new Error('No path parameters found')

  const { appId } = params
  const { getPage } = pageService(appId)
  const { getGlobalSetting } = settingService(appId)
  const [{ title, seoMetadataItemsCollection }, globalSetting] = await Promise.all([
    getPage('/', { locale }),
    getGlobalSetting({ locale }),
  ])

  return {
    props: {
      title: title ?? '',
      globalSetting,
      seoMetadataItemsCollection,
    },
    revalidate: 3600,
  }
}
